<template>
  <Field ref="numberInput"
         v-slot="{ field, meta }"
         v-model="localValue"
         :name="id"
         :label="validationLabel"
         :rules="rules"
         :validate-on-input="true"
         :validate-on-model-update="false">
    <TextInputGroup v-bind="field"
                    :id="id"
                    ref="input"
                    :value="localValue"
                    :disabled="disabled"
                    :placeholder="placeholder"
                    :name="validationLabel"
                    :state="readOnly || disabled ? null : getStateForComponentValidation(meta)"
                    :type="type"
                    :aria-describedby="`${id}-feedback`"
                    @blur="onBlur"
                    @focus="onFocus"
                    @input="onInput">
      <template v-for="(_, slot) of $slots"
                #[slot]="scope">
        <slot :name="slot"
              v-bind="scope"></slot>
      </template>
    </TextInputGroup>
  </Field>
  <small v-if="displayHelp"
         class="form-text text-muted w-100">{{ placeholder }}</small>
  <ErrorMessage :name="id"
                as="div"
                class="text-danger mt-1"></ErrorMessage>
</template>

<script>
  import { v4 as uuid } from 'uuid';
  import { defineComponent } from 'vue';
  import { ErrorMessage, Field } from 'vee-validate/compat';
  import TextInputGroup from './TextInputGroup.vue';
  import LocalizationService from '~/zf/frontend/common/localization/LocalizationService';
  import { translator } from '~/zf/frontend/common/mixins';
  import validation from '../mixins/validation';
  import Validations from '~/zf/frontend/common/veeValidate/Validations';
  import Validator from '../veeValidate/Validator';

  export default defineComponent({
    name: 'NumberInput',
    compatConfig: {
      MODE: 3,
    },
    components: {
      TextInputGroup,
      Field,
      ErrorMessage,
    },
    mixins: [translator, validation],
    props: {
      disabled: {
        default: false,
        type: Boolean,
      },
      id: {
        type: String,
        default: () => `number-input-${uuid()}`,
      },
      maximumFractionDigits: {
        default: 2,
        type: Number,
      },
      readOnly: {
        type: Boolean,
        default: false,
      },
      value: {
        type: [Number, null],
      },
      validationLabel: {
        type: String,
        default: () => '',
      },
      required: {
        default: true,
        type: Boolean,
      },
      minimumValue: {
        default: null,
        type: Number,
      },
      maximumValue: {
        default: null,
        type: Number,
      },
      customValidationRules: {
        type: Object,
        default: () => ({}),
      },
      ignoreValidationOnEmit: {
        type: Boolean,
        default: false,
      },
      type: {
        type: String,
        default: 'text',
      },
    },
    emits: ['update', 'input'],
    data() {
      return {
        isFocused: false,
        localValue: null,
        modelValue: null,
        parsedValue: null,
      };
    },
    computed: {
      decimalSeparator() {
        return this.maximumFractionDigits > 0 ? LocalizationService.getDecimalSeparator() : '';
      },
      displayHelp() {
        return this.isFocused && this.localValue?.length > 0;
      },
      placeholder() {
        const format = this.tr('javascript.TX_FORMAT');
        const decimalPlaces = '0'.repeat(this.maximumFractionDigits);
        return `${format}: 1234${this.decimalSeparator}${decimalPlaces}`;
      },
      regex() {
        return Validations.getDecimalValidatorRegexp(this.maximumFractionDigits, this.decimalSeparator);
      },
      rules() {
        if (this.disabled) {
          return null;
        }
        return {
          ...(this.maximumFractionDigits === 0 ? Validator.INTEGER : {}),
          ...(this.required ? Validator.REQUIRED : {}),
          ...(this.minimumValue !== null && this.maximumValue === null ? Validator.MIN_VALUE(this.minimumValue) : {}),
          ...(this.maximumValue !== null && this.minimumValue === null ? Validator.MAX_VALUE(this.maximumValue) : {}),
          ...(this.maximumValue !== null && this.minimumValue !== null ? Validator.BETWEEN(this.minimumValue, this.maximumValue) : {}),
          ...(Validator.REGEX(this.regex)),
          ...this.customValidationRules,
        };
      },
    },
    watch: {
      value: {
        handler(value) {
          this.localValue = this.localizeNumber(value);
          this.modelValue = value;
          this.parsedValue = value;
        },
        immediate: true,
      },
    },
    methods: {
      localizeNumber(value) {
        if (value === null || value === undefined) {
          return '';
        }
        const options = {
          minimumFractionDigits: 0,
          maximumFractionDigits: this.maximumFractionDigits,
          useGrouping: false,
        };
        return LocalizationService.localizeNumberWithOptions(value, options);
      },
      onBlur() {
        setTimeout(() => { this.isFocused = false; }, 125);
      },
      onFocus() {
        this.isFocused = true;
        if (this.readOnly) {
          this.$refs.input.blur();
        }
      },
      async onInput(value) {
        const modifiedValue = value.replace(',', '.');
        this.parsedValue = modifiedValue === '' ? null : Number.parseFloat(modifiedValue);
        const validationResult = await this.$refs.numberInput.validate();

        if (!validationResult.valid && !this.ignoreValidationOnEmit) return;

        if (this.modelValue !== this.parsedValue && !Number.isNaN(this.parsedValue)) {
          this.modelValue = this.parsedValue;
          this.$emit('update', this.parsedValue);
        }
        this.$emit('input', value);
      },
    },
  });
</script>
